'use client';

import React, { useEffect, useState } from 'react';
import { registerContact } from '@/actions';
import { HubspotFormData } from '@/interfaces';
import { RichTextField } from '@prismicio/client';
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import cookies from 'js-cookie';
import { PrismicRichText } from '@prismicio/react';
import { CheckIcon, ArrowRightIcon } from '@/assets';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  emailRegex,
  isCorporateEmail,
  htmlToPlainText,
  resolveFieldPlaceholder,
} from '@/utils';

/**
 * Post-submit redirect behavior. Omit for no redirect (the success view is shown).
 * - `confirmation-page`: immediately navigate to `/uid` (+utm) — the success view is hidden.
 * - `link`: show the success message, then navigate to `url` after `delayMs` (default 3000).
 */
export type FormRedirect =
  | { mode: 'confirmation-page'; uid?: string }
  | { mode: 'link'; url: string; delayMs?: number };

interface Props {
  formData: HubspotFormData;
  hubspotPortalIdForm: string;
  hubspotGuidForm: string;
  formTitle?: RichTextField;
  formDescription?: RichTextField;
  renderTerms?: boolean;
  /** Post-submit redirect (see `FormRedirect`). Omit for none (success view shown). */
  redirect?: FormRedirect;
  utmEbook?: string;
  formContainerClass?: string;
  /**
   * When false, skip the corporate-email `validate` rule so the email-domain
   * policy is left to the HubSpot form's own configuration. Defaults to true to
   * preserve the existing behavior of every current caller.
   */
  enforceCorporateEmail?: boolean;
  /**
   * Custom consent label rendered next to the required `terms` checkbox. When
   * omitted, the default "I accept the Terms and conditions" link is shown.
   */
  termsContent?: RichTextField;
  /**
   * Render each field's HubSpot label above the input (defaults to off, i.e.
   * placeholder-only, matching current callers).
   */
  showFieldLabels?: boolean;
  /** Optional style overrides — each defaults to the current hardcoded class. */
  labelClassName?: string;
  inputClassName?: string;
  fieldRowClassName?: string;
  submitClassName?: string;
  /** Class for the consent label wrapper (defaults to white text). */
  termsClassName?: string;
  /** Class for checkbox-type inputs (single_checkbox / booleancheckbox). */
  checkboxClassName?: string;
  /**
   * Overrides the submit button label. Falls back to the HubSpot form's
   * configured `submitButtonText`, then to "Submit".
   */
  submitLabel?: string;
  /** Optional custom post-submit success content. */
  successContent?: RichTextField;
  /** Class for the post-submit success container. */
  successClassName?: string;
  /**
   * Optional content rendered at the bottom of the form (e.g. a footnote). It
   * lives inside the `<form>`, so it is automatically hidden once the form is
   * submitted and the success view replaces the form.
   */
  footer?: React.ReactNode;
}

const getFieldType = (fieldType: string) => {
  switch (fieldType) {
    case 'single_line_text':
    default:
      return 'text';
    case 'phone':
      return 'tel';
    case 'email':
      return 'email';
  }
};

// HubSpot boolean/consent field types that must render as a checkbox rather
// than a text input.
const CHECKBOX_FIELD_TYPES = new Set(['single_checkbox', 'booleancheckbox']);

export const FormLayout = ({
  formData,
  hubspotPortalIdForm,
  hubspotGuidForm,
  formTitle,
  formDescription,
  renderTerms = true,
  redirect,
  utmEbook = '',
  formContainerClass = 'flex flex-col gap-y-6',
  enforceCorporateEmail = true,
  termsContent,
  showFieldLabels = false,
  labelClassName = 'mb-2 block text-[16px] text-white',
  inputClassName = 'p-3 block w-full border border-gray-400 rounded-lg text-white outline-none bg-transparent placeholder-gray-300',
  fieldRowClassName = 'flex gap-4',
  submitClassName = 'secondary-button w-full disabled:opacity-50',
  termsClassName = 'cursor-pointer text-white',
  checkboxClassName = 'mt-1 h-4 w-4 shrink-0 cursor-pointer',
  submitLabel,
  successContent,
  successClassName = 'flex flex-col gap-y-6 items-center justify-center h-full text-center',
  footer,
}: Props) => {
  const { fieldGroups } = formData;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FieldValues>({ mode: 'onBlur' });
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleWindowClick = () => setOpenDropdown(null);
    window.addEventListener('click', handleWindowClick);
    return () => window.removeEventListener('click', handleWindowClick);
  }, []);

  // 'link' redirect mode: navigate after the success view has shown for delayMs
  // (default 3000). Kept separate from the 'confirmation-page' path so the
  // success message stays visible during the delay; timer cleared on unmount.
  useEffect(() => {
    if (!isSubmitSuccessful || redirect?.mode !== 'link') {
      return;
    }
    const { url, delayMs = 3000 } = redirect;
    const timeoutId = setTimeout(() => {
      const target = new URL(url, window.location.href);
      const samePage =
        target.origin === window.location.origin &&
        target.pathname === window.location.pathname;
      if (samePage) {
        // router.push to the current route is a no-op — force a reload instead.
        window.location.reload();
      } else if (target.origin !== window.location.origin) {
        window.location.href = target.href; // external → full-page navigation
      } else {
        // internal → SPA navigation
        router.push(target.pathname + target.search + target.hash);
      }
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [isSubmitSuccessful, redirect, router]);

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (executeRecaptcha) {
      try {
        const token = await executeRecaptcha('onSubmit');

        const response = await fetch('/api/validate_token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const verificationToken = await response.json();

        if (verificationToken.success && verificationToken.score > 0.6) {
          reset();

          delete data.terms;

          // Checkbox fields register as booleans; HubSpot's submit API expects
          // string values, so coerce them to "true"/"false".
          Object.keys(data).forEach((key) => {
            if (typeof data[key] === 'boolean') {
              data[key] = data[key] ? 'true' : 'false';
            }
          });

          const { postSubmitAction } = await registerContact({
            properties: data,
            pageUri: `https://devsu.com${window.location.pathname}`,
            pageName: window.document.title,
            hubspotPortalIdForm: hubspotPortalIdForm,
            hubspotGuidForm: hubspotGuidForm,
            hubspotutkCookie: cookies.get('hubspotutk'),
          });

          if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
            window.location.assign(postSubmitAction.value);
            return;
          }

          setIsSubmitSuccessful(true);

          if (utmEbook) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('utm', utmEbook);
            window.history.replaceState({}, '', currentUrl.toString());
          }

          if (redirect?.mode === 'confirmation-page') {
            const uid = redirect.uid ?? '';
            const url = utmEbook ? `/${uid}?utm=${utmEbook}` : `/${uid}`;
            router.push(url);
          }
        } else {
          setIsSubmitSuccessful(false);
        }
      } catch (error) {
        console.error('reCAPTCHA failed:', error);
        setErrorMessage('Something went wrong. Please try again later.');
        setIsSubmitSuccessful(false);
        return;
      }
    }
  };

  if (isSubmitSuccessful && redirect?.mode !== 'confirmation-page') {
    return (
      <div className={successClassName}>
        {successContent ? (
          <PrismicRichText field={successContent} />
        ) : (
          <>
            <CheckIcon />
            <p className="font-bold text-medium-normal">
              Your form has been received. Thanks for choosing Devsu!
            </p>
            <p>We&apos;ll reach out soon.</p>
            <p>
              While you wait, <span className="font-bold">check out our</span>{' '}
              <Link href="/resources-center">
                <span className="text-[#017a66] font-bold">blog.</span>
              </Link>
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={formContainerClass}
      noValidate
    >
      {formTitle && (
        <div className="text-left">
          <PrismicRichText field={formTitle} />
        </div>
      )}
      {formDescription && (
        <div className="text-left">
          <PrismicRichText
            field={formDescription}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
        </div>
      )}
      {fieldGroups.map(({ fields }, index) => (
        <div key={`field-group-${index}`} className={fieldRowClassName}>
          {fields.map(
            (
              { label, required, fieldType, name, placeholder, options },
              fieldIndex,
            ) => {
              const isCheckbox = CHECKBOX_FIELD_TYPES.has(fieldType);
              // HubSpot labels can carry rich-text markup — render them as text.
              const cleanLabel = htmlToPlainText(label);
              const fieldPlaceholder = resolveFieldPlaceholder({
                placeholder,
                label,
                fieldType,
              });

              return (
                <div
                  key={`field-${fieldIndex}`}
                  className={isCheckbox ? 'flex-1 sm:col-span-2' : 'flex-1'}
                >
                  {showFieldLabels && label && !isCheckbox && (
                    <label htmlFor={name} className={labelClassName}>
                      {cleanLabel}
                    </label>
                  )}
                  {isCheckbox ? (
                    <>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id={name}
                          className={checkboxClassName}
                          {...register(name, {
                            required: {
                              value: required,
                              message: 'This field is required',
                            },
                          })}
                          aria-label={cleanLabel}
                        />
                        <label htmlFor={name} className={termsClassName}>
                          {cleanLabel}
                        </label>
                      </div>
                      {errors[name] && (
                        <span className="text-error text-sm mt-1 px-3">
                          {errors[name]?.message as string}
                        </span>
                      )}
                    </>
                  ) : fieldType === 'dropdown' && options ? (
                    <>
                      <Controller
                        name={name}
                        control={control}
                        rules={{
                          required: {
                            value: required,
                            message: `${cleanLabel} is required`,
                          },
                        }}
                        render={({ field: { value, onChange } }) => {
                          const selected = options.find(
                            (o) => o.value === value,
                          );
                          const isOpen = openDropdown === name;
                          return (
                            <div
                              className="relative"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                aria-haspopup="listbox"
                                aria-expanded={isOpen}
                                aria-controls={
                                  isOpen ? `${name}-listbox` : undefined
                                }
                                onClick={() =>
                                  setOpenDropdown(isOpen ? null : name)
                                }
                                className="p-3 w-full border border-gray-400 text-white rounded-lg bg-transparent outline-none text-left"
                              >
                                <span
                                  className={selected ? '' : 'text-gray-300'}
                                >
                                  {selected
                                    ? htmlToPlainText(selected.label)
                                    : fieldPlaceholder}
                                </span>
                              </button>
                              {isOpen && (
                                <ul
                                  id={`${name}-listbox`}
                                  role="listbox"
                                  className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-500 bg-[#0d1918] shadow-lg"
                                >
                                  {options.map((option, idx) => {
                                    const isSelected = value === option.value;
                                    return (
                                      <li
                                        key={idx}
                                        id={`${name}-option-${option.value}`}
                                        role="option"
                                        aria-selected={isSelected}
                                      >
                                        <button
                                          type="button"
                                          className={`w-full text-left px-3 py-2 hover:bg-white/10 ${
                                            isSelected ? 'bg-white/10' : ''
                                          }`}
                                          onClick={() => {
                                            onChange(option.value);
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          {htmlToPlainText(option.label)}
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        }}
                      />
                      {errors[name] && (
                        <span className="text-error text-sm mt-1 px-3">
                          {errors[name]?.message as string}
                        </span>
                      )}
                    </>
                  ) : name === 'scope' ? (
                    <>
                      <textarea
                        rows={4}
                        placeholder={fieldPlaceholder}
                        className="mt-1 p-3 block w-full border border-gray-400 rounded-md text-white outline-none bg-transparent placeholder-gray-300"
                        {...register(name, {
                          required: {
                            value: required,
                            message: `${cleanLabel} is required`,
                          },
                        })}
                        aria-label={cleanLabel}
                      />
                      {errors[name] && (
                        <span className="text-error text-sm mt-1 px-3">
                          {errors[name]?.message as string}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type={getFieldType(fieldType)}
                        id={name}
                        placeholder={fieldPlaceholder}
                        {...register(name, {
                          required: {
                            value: required,
                            message: `${cleanLabel} is required`,
                          },
                          ...(fieldType === 'email' && {
                            pattern: {
                              value: emailRegex,
                              message: 'Enter a valid email',
                            },
                            ...(enforceCorporateEmail && {
                              validate: {
                                isCorporate: (value) =>
                                  isCorporateEmail(value) ||
                                  'Please use a corporate email address',
                              },
                            }),
                          }),
                        })}
                        className={inputClassName}
                        aria-label={cleanLabel}
                      />
                      {errors[name] && (
                        <span className="text-error text-sm mt-1 px-3">
                          {errors[name]?.message as string}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            },
          )}
        </div>
      ))}
      {renderTerms && (
        <div className="flex flex-col">
          <div className="flex items-center gap-x-4">
            <input
              type="checkbox"
              id="terms"
              {...register('terms', {
                required: {
                  value: true,
                  message: 'You should accept Terms and Conditions',
                },
              })}
              className="cursor-pointer"
              aria-label="Accept Terms and Conditions"
            />
            <label htmlFor="terms" className={termsClassName}>
              {termsContent ? (
                <PrismicRichText
                  field={termsContent}
                  components={{
                    paragraph: ({ children }) => <span>{children}</span>,
                  }}
                />
              ) : (
                <>
                  I accept the{' '}
                  <Link
                    href="/terms-of-service"
                    className="text-green-400 underline hover:text-green-300"
                  >
                    Terms and conditions
                  </Link>
                  .
                </>
              )}
            </label>
          </div>
          {errors['terms'] && (
            <span className="text-error text-sm mt-1">
              {errors['terms']?.message as string}
            </span>
          )}
        </div>
      )}

      <button
        className={submitClassName}
        type="submit"
        disabled={isSubmitting}
        aria-label="Submit form"
      >
        <ArrowRightIcon className="arrow-icon" />
        <span>
          {submitLabel || formData.displayOptions.submitButtonText || 'Submit'}
        </span>
      </button>

      {errorMessage && (
        <span className="text-error text-sm mt-1">{errorMessage}</span>
      )}

      {footer}
    </form>
  );
};
